import { useState } from 'react';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { emailImportsApi, type AnalyzeResult } from '../api';
import { Loader2, Upload, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

type Step = 'upload' | 'preview' | 'result';

export default function EmailImportWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [sourceType, setSourceType] = useState('CSV_IMPORT');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [importId, setImportId] = useState<number | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<number | null>(null);
  const [analysis, setAnalysis] = useState<AnalyzeResult | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [resultStatus, setResultStatus] = useState<string | null>(null);

  const doUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await emailImportsApi.upload(file, sourceType, description || undefined);
      setImportId(res.importId);
      setDuplicateWarning(res.duplicateOfImportId);
      const preview = await emailImportsApi.analyze(res.importId);
      setAnalysis(preview);
      setStep('preview');
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.response?.data?.error?.message || error?.message || 'No se pudo procesar el archivo.',
      });
    } finally {
      setUploading(false);
    }
  };

  const doConfirm = async () => {
    if (!importId) return;
    setConfirming(true);
    try {
      const result = await emailImportsApi.confirm(importId);
      setResultStatus(result.status);
      setStep('result');
      toast({ title: 'Importación confirmada' });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.response?.data?.error?.message || 'No se pudo confirmar la importación.',
      });
    } finally {
      setConfirming(false);
    }
  };

  const doCancel = async () => {
    if (!importId) return;
    try {
      await emailImportsApi.cancel(importId);
      toast({ title: 'Importación cancelada' });
      navigate('/admin/email-contacts/imports');
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo cancelar.' });
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 space-y-6 max-w-3xl">
        <Link to="/admin/email-contacts/imports">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" /> Volver
          </Button>
        </Link>

        <div>
          <h1 className="text-3xl font-bold">Importar contactos</h1>
          <p className="text-muted-foreground">Suba un archivo CSV con emails para agregarlos a la base consolidada.</p>
        </div>

        {step === 'upload' && (
          <Card className="p-6 space-y-4">
            <div className="space-y-2">
              <Label>Archivo CSV</Label>
              <Input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
            <div className="space-y-2">
              <Label>Origen</Label>
              <Input value={sourceType} onChange={(e) => setSourceType(e.target.value)} placeholder="FIREBASE_IMPORT, CSV_IMPORT, ..." />
            </div>
            <div className="space-y-2">
              <Label>Descripción (opcional)</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ej: Base histórica Firebase - exportación anterior del Sabuesos"
              />
            </div>
            <Button onClick={doUpload} disabled={!file || uploading}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Subir y analizar
            </Button>
          </Card>
        )}

        {step === 'preview' && analysis && (
          <div className="space-y-4">
            {duplicateWarning && (
              <Card className="p-4 bg-yellow-50 border-yellow-300">
                <p className="text-sm">
                  Este archivo parece haber sido importado anteriormente (import #{duplicateWarning}). Puede continuar de
                  todos modos si lo desea.
                </p>
              </Card>
            )}

            <Card className="p-6 space-y-4">
              <h2 className="text-lg font-semibold">Resumen del archivo</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <Stat label="Líneas encontradas" value={analysis.totalRows} />
                <Stat label="Emails válidos" value={analysis.validRows} />
                <Stat label="Emails inválidos" value={analysis.invalidRows} />
                <Stat label="Duplicados en el archivo" value={analysis.duplicateRows} />
                <Stat label="Nuevos contactos" value={analysis.newContacts} />
                <Stat label="Ya existentes" value={analysis.existingContacts} />
                <Stat label="Suprimidos/Unsubscribed" value={analysis.suppressedContacts} />
              </div>
            </Card>

            <Card className="p-6 space-y-3">
              <h2 className="text-lg font-semibold">Muestra de registros</h2>
              <div className="space-y-1 max-h-64 overflow-y-auto text-sm">
                {analysis.sample.map((row) => (
                  <div key={row.row} className="flex items-center justify-between border-b py-1">
                    <span className="truncate">{row.email || '(vacío)'}</span>
                    <Badge variant="outline">{row.classification}</Badge>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6 space-y-3 bg-muted/40">
              <p className="text-sm font-medium">Esta operación irá a:</p>
              <ul className="text-sm list-disc list-inside space-y-1">
                <li>Crear {analysis.newContacts} nuevos contactos.</li>
                <li>Asociar el origen a {analysis.existingContacts} contactos existentes.</li>
                <li>Ignorar {analysis.invalidRows} emails inválidos.</li>
                <li>Preservar {analysis.suppressedContacts} contactos suprimidos/unsubscribed sin reactivarlos.</li>
              </ul>
              <p className="text-sm font-semibold">Ningún email será enviado en esta operación.</p>
            </Card>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={doCancel}>
                Cancelar
              </Button>
              <Button onClick={doConfirm} disabled={confirming}>
                {confirming ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Confirmar importación
              </Button>
            </div>
          </div>
        )}

        {step === 'result' && (
          <Card className="p-6 space-y-4 text-center">
            <h2 className="text-xl font-semibold">Importación {resultStatus === 'COMPLETED' ? 'completada' : 'completada con errores'}</h2>
            <div className="flex justify-center gap-2">
              <Link to="/admin/email-contacts">
                <Button>Ver contactos</Button>
              </Link>
              <Link to="/admin/email-contacts/imports">
                <Button variant="outline">Ver historial de importaciones</Button>
              </Link>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p className="text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold">{value}</p>
    </div>
  );
}
